import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  LocalHospital as CaseIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Warning as EmergencyIcon,
  CheckCircle as ClosedIcon,
  Pending as OpenIcon,
  TrendingUp as TrendingIcon,
  Assignment as AssignmentIcon,
  MedicalServices as MedicalIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface CaseListItem {
  id: number;
  caseNumber: string;
  openedAt: string;
  closedAt: string | null;
  status: string;
  statusColor: string;
  caseType: string;
  caseTypeColor: string;
  priority: string;
  priorityColor: string;
  officerId: number;
  officerName: string;
  officerCadre: string;
  doctorId: number;
  doctorName: string;
  doctorSpecialization: string;
  bookingNumber: string;
  chiefComplaint: string;
  prescriptionStatus: string;
  visitAddress: string;
  visitCity: string;
  createdAt: string;
}

interface CaseStatistics {
  totalCasesToday: number;
  openCasesToday: number;
  closedCasesToday: number;
  emergencyCasesToday: number;
  totalCases: number;
  openCases: number;
  closedCases: number;
  pendingDiagnosis: number;
  pendingPrescription: number;
  statusDistribution: { status: string; displayName: string; count: number; color: string }[];
  typeDistribution: { type: string; displayName: string; count: number; color: string }[];
  priorityDistribution: { priority: string; displayName: string; count: number; color: string }[];
  avgResolutionTimeHours: number;
}

const CaseManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [statistics, setStatistics] = useState<CaseStatistics | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCase, setSelectedCase] = useState<CaseListItem | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        search: searchQuery,
        status: statusFilter,
        priority: priorityFilter,
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/cases?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCases(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, priorityFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/cases/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchCases();
    fetchStatistics();
  }, [fetchCases, fetchStatistics]);

  const handleActionOpen = (event: React.MouseEvent<HTMLElement>, caseItem: CaseListItem) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedCase(caseItem);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedCase(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return <OpenIcon />;
      case 'VISITED': return <MedicalIcon />;
      case 'CLOSED': return <ClosedIcon />;
      default: return <OpenIcon />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'In Progress';
      case 'VISITED': return 'Visited';
      case 'CLOSED': return 'Completed';
      default: return status;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'LOW': return 'Low';
      case 'NORMAL': return 'Normal';
      case 'HIGH': return 'High';
      case 'URGENT': return 'Urgent';
      case 'EMERGENCY': return 'Emergency';
      default: return priority;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'HOME_VISIT': return 'Home Visit';
      case 'VIDEO_CONSULTATION': return 'Video Consultation';
      case 'EMERGENCY_VISIT': return 'Emergency';
      case 'FOLLOW_UP': return 'Follow-up';
      default: return type;
    }
  };

  const statCards = statistics ? [
    { title: "Today's Cases", value: statistics.totalCasesToday, icon: <CaseIcon />, color: '#2563eb' },
    { title: 'Open Today', value: statistics.openCasesToday, icon: <OpenIcon />, color: '#f59e0b' },
    { title: 'Closed Today', value: statistics.closedCasesToday, icon: <ClosedIcon />, color: '#10b981' },
    { title: 'Emergency', value: statistics.emergencyCasesToday, icon: <EmergencyIcon />, color: '#ef4444' },
    { title: 'Total Open', value: statistics.openCases, icon: <AssignmentIcon />, color: '#8b5cf6' },
    { title: 'Avg Resolution', value: `${statistics.avgResolutionTimeHours.toFixed(1)}h`, icon: <TimeIcon />, color: '#06b6d4' },
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Case Management Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Track and manage all medical cases
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchCases(); fetchStatistics(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: `${stat.color}15`,
                    color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Status Distribution */}
      {statistics && statistics.statusDistribution.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Status Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statistics.statusDistribution.map((item, index) => (
                <Chip
                  key={index}
                  label={`${item.displayName}: ${item.count}`}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 500,
                    bgcolor: `${item.color}15`,
                    color: item.color,
                    border: `1px solid ${item.color}40`,
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label={`All Cases (${totalElements})`} sx={{ textTransform: 'none' }} />
          <Tab label="Open" sx={{ textTransform: 'none' }} />
          <Tab label="In Progress" sx={{ textTransform: 'none' }} />
          <Tab label="Closed" sx={{ textTransform: 'none' }} />
          <Tab label="Emergency" sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Search & Filter */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            size="small"
            placeholder="Search cases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="VISITED">Visited</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="NORMAL">Normal</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="URGENT">Urgent</MenuItem>
              <MenuItem value="EMERGENCY">Emergency</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Case #</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Opened</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Patient</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Prescription</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : cases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: '#64748b' }}>No cases found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                cases.map((caseItem) => (
                  <TableRow key={caseItem.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {caseItem.caseNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {caseItem.bookingNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {caseItem.openedAt ? new Date(caseItem.openedAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                          {caseItem.officerName?.charAt(0) || 'O'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {caseItem.officerName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {caseItem.officerCadre}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                          {caseItem.doctorName?.charAt(0) || 'D'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {caseItem.doctorName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {caseItem.doctorSpecialization}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(caseItem.caseType)}
                        size="small"
                        sx={{
                          bgcolor: `${caseItem.caseTypeColor}15`,
                          color: caseItem.caseTypeColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPriorityLabel(caseItem.priority)}
                        size="small"
                        sx={{
                          bgcolor: `${caseItem.priorityColor}15`,
                          color: caseItem.priorityColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(caseItem.status)}
                        label={getStatusLabel(caseItem.status)}
                        size="small"
                        sx={{
                          bgcolor: `${caseItem.statusColor}15`,
                          color: caseItem.statusColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={caseItem.prescriptionStatus}
                        size="small"
                        sx={{
                          bgcolor: caseItem.prescriptionStatus === 'COMPLETED' ? '#10b98115' : '#f59e0b15',
                          color: caseItem.prescriptionStatus === 'COMPLETED' ? '#10b981' : '#f59e0b',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleActionOpen(e, caseItem)}>
                        <TimeIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><CaseIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Patient</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><MedicalIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Prescription</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CaseManagementPage;